import { ArrowRight, Clock, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { MarketingShell } from './MarketingShell';
import { Reveal } from './Reveal';
import { useBrand } from '../../brand/BrandProvider';
import { useCms, cmsText } from '../../api/cms';
import { isItcityHost } from '../../brand/host';
import { SINGLE_FALLBACKS } from '../../content/fallbacks';

/**
 * Public contact page — brand-aware (Decision #21). The email card always shows
 * the host brand's support mailbox; phone prefers the CMS override, then the
 * brand support phone. Address/hours are CMS-only (`contact.info`) so nothing
 * is invented: an unset row is hidden. On the PLATFORM hosts the real ITCity
 * details are injected as defaults (same brand-computed-fallback pattern as the
 * pricing subhead); a reseller fills their own via Content → Contact page.
 */
export function Contact() {
  const brand = useBrand();
  const isReseller = !brand.isPlatform;
  const { data: cms } = useCms();
  const f = SINGLE_FALLBACKS['contact.info']!;

  // /contact resolves on BOTH platform hosts off one shared key, so an edit for
  // possignin.com rewrote itcity.ltd too. ITCity reads its own key first, per field,
  // falling back to the Possignin value — inert until ITCity is given its own details.
  const itcity = isItcityHost();
  const cText = (field: string, dflt: string) =>
    itcity
      ? cmsText(cms, 'company.contactInfo', field, cmsText(cms, 'contact.info', field, dflt))
      : cmsText(cms, 'contact.info', field, dflt);

  const headline = cText('headline', f.headline!);
  const subhead = cText('subhead', f.subhead!);
  const phone = cText('phone', '') || brand.supportPhone || '';
  const address = cText('address', isReseller ? '' : 'ITCity — Sylhet, Bangladesh');
  const hours = cText('hours', isReseller ? '' : 'Saturday–Thursday, 10:00–18:00 (Dhaka time)');

  const cards: { icon: typeof Mail; label: string; value: string; href?: string }[] = [
    { icon: Mail, label: 'Email', value: brand.supportEmail, href: `mailto:${brand.supportEmail}` },
    ...(phone.trim() !== '' ? [{ icon: Phone, label: 'Phone', value: phone, href: `tel:${phone.replace(/[^+\d]/g, '')}` }] : []),
    ...(address.trim() !== '' ? [{ icon: MapPin, label: 'Address', value: address }] : []),
    ...(hours.trim() !== '' ? [{ icon: Clock, label: 'Hours', value: hours }] : []),
  ];

  return (
    <MarketingShell>
      <section className="relative overflow-hidden">
        <div className="glow-bg absolute inset-x-0 top-0 h-[24rem] -z-10 pointer-events-none" aria-hidden="true" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-10 text-center">
          <Reveal>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
              {headline}
            </h1>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed text-pretty">{subhead}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-8">
        <div className={`grid gap-4 sm:grid-cols-2 ${cards.length >= 3 ? 'lg:grid-cols-2' : ''}`}>
          {cards.map((c, i) => {
            const Icon = c.icon;
            const body = (
              <>
                <span className="w-10 h-10 shrink-0 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{c.label}</span>
                  <span className="mt-1 block font-medium text-zinc-950 dark:text-zinc-50 break-words whitespace-pre-line">{c.value}</span>
                </span>
              </>
            );
            const cls =
              'flex items-start gap-4 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-5 sm:p-6';
            return (
              <Reveal key={c.label} delay={i * 60}>
                {c.href ? (
                  <a href={c.href} className={`${cls} transition-colors hover:border-zinc-300 dark:hover:border-zinc-700`}>
                    {body}
                  </a>
                ) : (
                  <div className={cls}>{body}</div>
                )}
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 text-center">
        <Reveal>
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/40 p-8 sm:p-10">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
              Not a customer yet?
            </h2>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
              Try {brand.name} free for 7 days — no card needed, full product.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/signup"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand-hover shadow-sm transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
              >
                Start free trial
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/pricing"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
              >
                See pricing
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </MarketingShell>
  );
}
